import React from "react";

import {
  OfferCountdownContainer,
  OfferCountdownLabel,
  OfferCountdownValue,
} from "./OfferCountdown.styles";
import { Icon } from "../../icons";
import { UserBookingInterest } from "../../models/BookingInterest";

/** Below this much time left the pill turns red — the point where minutes actually matter. */
const URGENT_THRESHOLD_MS = 60 * 60 * 1000;

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

/**
 * The deadline this booking is currently counting down to, or null when it is not waiting on
 * one. Only a LIVE offer has a deadline worth showing:
 *
 * - `InvitedToTicket` — the email offer, expires at `invitedToTicketExpiration`
 * - `AttendanceSMSReminderSent` — the check-in / last-min SMS offer, expires at
 *   `attendanceExpiration` (the consolidated flow judges these per offer, so this is the real
 *   deadline even when it falls after the session-wide bulk check-in window)
 *
 * Everything else — applications waiting in a queue, accepted tickets, declined, revoked, and
 * offers that have already lapsed — has nothing pending, so the countdown stays hidden.
 */
export const getOfferDeadline = (bookingInterest: UserBookingInterest): Date | null => {
  const raw =
    bookingInterest.status === "InvitedToTicket"
      ? bookingInterest.invitedToTicketExpiration
      : bookingInterest.status === "AttendanceSMSReminderSent"
        ? bookingInterest.attendanceExpiration
        : null;

  if (!raw) {
    return null;
  }

  const deadline = new Date(raw);
  return Number.isNaN(deadline.getTime()) ? null : deadline;
};

/**
 * Days and hours far out, then a ticking h:mm:ss inside the last day — a seconds display on a
 * two-day offer is noise, and a minutes-only one in the last hour hides exactly the urgency
 * this is here to convey.
 */
const formatRemaining = (remainingMs: number): string => {
  if (remainingMs >= DAY_MS) {
    const days = Math.floor(remainingMs / DAY_MS);
    const hours = Math.floor((remainingMs % DAY_MS) / HOUR_MS);
    return `${days}d ${hours}h`;
  }

  const totalSeconds = Math.floor(remainingMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const padded = `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;

  return hours > 0 ? `${hours}:${padded}` : padded;
};

/**
 * Milliseconds left on a deadline, ticking once a second; null when there is no deadline.
 *
 * Shared rather than duplicated because two screens need the same clock for different
 * reasons: the booking card renders it, and the last-minute offer page uses it to take the
 * Accept button away the moment the offer lapses. A page that showed a live countdown next to
 * a button that still submitted an expired offer would be worse than showing neither.
 */
export const useRemainingMs = (deadlineMs: number | null): number | null => {
  // A tick, not the remainder itself. Holding the remainder in state means every render
  // between "the deadline arrived" and "the effect re-seeded it" reports the PREVIOUS
  // deadline's leftovers — seeded at 0 on the first mount, which reads as "expired" and is
  // enough to flash an expiry notice over a live offer. Deriving it below cannot go stale.
  const [, tick] = React.useReducer((count: number) => count + 1, 0);

  React.useEffect(() => {
    if (deadlineMs === null || deadlineMs - Date.now() <= 0) {
      return;
    }

    const interval = setInterval(() => {
      tick();

      // Nothing left to count — stop rather than re-render every second forever. The status
      // itself only changes once the backend sweep runs, which can be a poll or two later.
      if (deadlineMs - Date.now() <= 0) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [deadlineMs]);

  return deadlineMs === null ? null : deadlineMs - Date.now();
};

export interface OfferCountdownProps {
  bookingInterest: UserBookingInterest;
  /** Sizes the pill up for a page that is only about this one offer. */
  size?: "sm" | "lg";
}

/**
 * Live countdown to the moment the current offer lapses (TTT-687/TTT-688).
 *
 * Renders nothing at all unless this booking is holding an offer with a deadline still in the
 * future, so a card with nothing expiring is untouched.
 */
export const OfferCountdown = ({ bookingInterest, size = "sm" }: OfferCountdownProps) => {
  const deadline = getOfferDeadline(bookingInterest);
  const deadlineMs = deadline?.getTime() ?? null;
  const remainingMs = useRemainingMs(deadlineMs);

  if (deadlineMs === null || remainingMs === null || remainingMs <= 0) {
    return null;
  }

  const urgent = remainingMs < URGENT_THRESHOLD_MS;
  const label =
    bookingInterest.status === "AttendanceSMSReminderSent" ? "Check-in closes in" : "Offer expires in";

  return (
    <OfferCountdownContainer urgent={urgent} size={size} data-cy="offer-countdown" data-cy-urgent={urgent}>
      <Icon icon="Clock" size="sm" />
      <OfferCountdownLabel urgent={urgent} size={size}>{label}</OfferCountdownLabel>
      <OfferCountdownValue urgent={urgent} size={size} data-cy="offer-countdown-value">
        {formatRemaining(remainingMs)}
      </OfferCountdownValue>
    </OfferCountdownContainer>
  );
};

OfferCountdown.displayName = "OfferCountdown";
